import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { Dropdown } from 'primereact/dropdown';
import { fetchURL } from 'src/util/api';
import { RootReducerState } from 'src/store/reducers';
import {
    getEmployeeNumList,
    getDealerNumList,
    getCustomerNumList,
    getFranchiseNumList,
    getSupplierNumList,
} from 'src/store/WhatsApp/action';
import './WhatsappChat.css';

interface Contact {
    id: string;
    phoneNumber: string;
    contactName: string;
    lastMessageTime: string;
    lastMessageText: string;
    unreadCount: number;
}

interface Message {
    id: string;
    messageId: string;
    sender: string;
    messageText: string;
    messageType: string;
    attachmentUrl: string;
    attachmentName: string;
    status: string;
    creationTime: string;
}

const WhatsappChat = () => {
    const dispatch = useDispatch();
    const { employeeNum, dealerNum, customerNum, franchiseNum, supplierNum } = useSelector(
        (state: RootReducerState) => state.WhatsappReducer
    );

    const [contacts, setContacts] = useState<Contact[]>([]);

    // New Chat Modal States
    const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
    const [selectedUserType, setSelectedUserType] = useState("");
    const [contactOptions, setContactOptions] = useState<{ name: string, phone: string }[]>([]);
    const [selectedNewContactPhone, setSelectedNewContactPhone] = useState("");
    const [isCreatingChat, setIsCreatingChat] = useState(false);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [replyText, setReplyText] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Filter contacts by name or phone based on search query
    const filteredContacts = contacts.filter(c => {
        const q = searchQuery.toLowerCase();
        return (
            (c.contactName || '').toLowerCase().includes(q) ||
            (c.phoneNumber || '').includes(q)
        );
    });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchContactsList = () => {
        fetchURL("whats-app-chat-notification/chats", { method: "GET" }, "whatsApp")
            .then((res: any) => {
                const responseData = res?.data || [];
                setContacts(Array.isArray(responseData) ? responseData : (responseData?.items || []));
            })
            .catch((err: any) => console.error("Error fetching contacts", err));
    };

    useEffect(() => {
        fetchContactsList();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Handle New Chat Modal open
    useEffect(() => {
        if (isNewChatModalOpen) {
            dispatch(getEmployeeNumList());
            dispatch(getDealerNumList());
            dispatch(getCustomerNumList());
            dispatch(getFranchiseNumList());
            dispatch(getSupplierNumList());
        }
    }, [isNewChatModalOpen, dispatch]);

    useEffect(() => {
        if (selectedUserType === "Employees") setContactOptions(employeeNum as any || []);
        else if (selectedUserType === "Dealer") setContactOptions(dealerNum as any || []);
        else if (selectedUserType === "Customer") setContactOptions(customerNum as any || []);
        else if (selectedUserType === "Franchise") setContactOptions(franchiseNum as any || []);
        else if (selectedUserType === "Supplier") setContactOptions(supplierNum as any || []);
        else setContactOptions([]);
        setSelectedNewContactPhone("");
    }, [selectedUserType, employeeNum, dealerNum, customerNum, franchiseNum, supplierNum]);

    const handleCreateNewChat = () => {
        if (!selectedNewContactPhone) return;
        setIsCreatingChat(true);
        const selectedContactDetails = contactOptions.find(c => c.phone === selectedNewContactPhone);
        const contactName = selectedContactDetails?.name || selectedNewContactPhone;

        fetchURL("whats-app-chat-notification/chat", {
            method: "POST",
            body: {
                phoneNumber: selectedNewContactPhone,
                contactName: contactName
            }
        }, "whatsApp")
            .then((res: any) => {
                fetchContactsList();
                setIsNewChatModalOpen(false);
                setSelectedUserType("");
                setSelectedNewContactPhone("");
            })
            .catch((err: any) => console.error("Error creating chat", err))
            .finally(() => setIsCreatingChat(false));
    };

    const fetchMessages = (contactId: string, clearFirst: boolean = true) => {
        if (clearFirst) setMessages([]);
        fetchURL(`whats-app-chat-notification/chat-messages/${contactId}`, { method: "GET" }, "whatsApp")
            .then((res: any) => {
                const responseData = res?.data || [];
                setMessages(Array.isArray(responseData) ? responseData : (responseData?.items || []));
            })
            .catch((err: any) => console.error("Error fetching messages", err));
    };

    const handleContactClick = (contact: Contact) => {
        setSelectedContact(contact);
        fetchMessages(contact.id, true);

        // Mark the chat as read
        fetchURL(`whats-app-chat-notification/mark-as-read/${contact.id}`, { method: "POST" }, "whatsApp")
            .then(() => {
                // Refresh contacts list to update unread counts
                fetchContactsList();
            })
            .catch((err: any) => console.error("Error marking chat as read", err));
    };

    const handleSendReply = () => {
        if (!replyText.trim() || !selectedContact) return;

        const messageToSend = replyText;
        setReplyText("");

        // Create optimistic message
        const newMessage: Message = {
            id: Date.now().toString(),
            messageId: "temp",
            sender: "me", // Assume "user" means the person replying from this UI
            messageText: messageToSend,
            messageType: "text",
            attachmentUrl: "",
            attachmentName: "",
            status: "sending",
            creationTime: new Date().toISOString()
        };

        setMessages(prev => [...prev, newMessage]);

        fetchURL("whats-app-chat-notification/send-message", {
            method: "POST",
            body: {
                whatsAppChatNotificationId: selectedContact.id,
                messageText: messageToSend,
                messageType: "text",
                attachmentUrl: "",
                attachmentName: "",
                attachmentMimeType: ""
            }
        }, "whatsApp")
            .then(() => {
                // Refresh both the contacts list (for last message preview) and the chat messages
                fetchContactsList();
                fetchMessages(selectedContact.id, false);
            })
            .catch((err: any) => {
                console.error("Error sending message", err);
                setMessages(prev =>
                    prev.map(msg => msg.id === newMessage.id ? { ...msg, status: "failed" } : msg)
                );
            });
    };

    // Helper to determine if a message was sent by the current user (the admin/system)
    // Depends on the 'sender' format from the API. Adjust condition if needed.
    const isSentByMe = (sender: string) => sender && sender.toLowerCase() === 'me';

    // Avatar color class based on name
    const getAvatarColor = (name: string) => {
        const colors = 8;
        let hash = 0;
        for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return `color-${Math.abs(hash) % colors}`;
    };

    const formatTime = (iso: string) => {
        try {
            const d = new Date(iso);
            const now = new Date();
            const isToday = d.toDateString() === now.toDateString();
            if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
        } catch { return ''; }
    };

    const formatMsgTime = (iso: string) => {
        try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
        catch { return ''; }
    };

    const formatDateDivider = (iso: string) => {
        try {
            if (!iso) return '';
            const d = new Date(iso);
            const now = new Date();
            
            if (d.toDateString() === now.toDateString()) return 'Today';
            
            const yesterday = new Date();
            yesterday.setDate(now.getDate() - 1);
            if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
            
            return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
        } catch { return ''; }
    };

    const getStatusIcon = (status: string) => {
        if (status === 'sending') return <span className="wac-msg-status">⏱</span>;
        if (status === 'failed') return <span className="wac-msg-status failed">✕</span>;
        if (status === 'sent') return <span className="wac-msg-status sent">✓✓</span>;
        return <span className="wac-msg-status">✓</span>;
    };

    return (
        <React.Fragment>
            <div className="page-content" style={{ paddingBottom: 0 }}>
                {/* wac-page-root fills the vertical space that page-content provides */}
                <div className="wac-page-root">
                    <div className="wac-container">

                        {/* ── Sidebar ── */}
                        <div className="wac-sidebar">
                            <div className="wac-sidebar-header">
                                <span className="wac-sidebar-title">
                                    <i className="bx bxl-whatsapp" style={{ fontSize: 20, marginRight: 8, verticalAlign: 'middle' }}></i>
                                    Chats
                                </span>
                                <button
                                    className="wac-new-chat-btn"
                                    onClick={() => setIsNewChatModalOpen(true)}
                                    title="New Chat"
                                >
                                    <i className="bx bx-plus"></i>
                                </button>
                            </div>

                            <div className="wac-search-bar">
                                <div className="wac-search-input-wrap">
                                    <i className="bx bx-search wac-search-icon"></i>
                                    <input
                                        className="wac-search-input"
                                        placeholder="Search contacts..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="wac-contact-list">
                                {filteredContacts.length === 0 ? (
                                    <div className="wac-empty-contacts">
                                        <div className="wac-empty-icon">
                                            <i className="bx bx-message-rounded-dots" style={{ fontSize: 42, color: '#128c7e' }}></i>
                                        </div>
                                        <p className="wac-empty-text">{searchQuery ? 'No results found' : 'No chats yet'}</p>
                                        <p className="wac-empty-sub">{searchQuery ? `No contacts match "${searchQuery}"` : 'Click the edit icon to start a conversation'}</p>
                                    </div>
                                ) : (
                                    filteredContacts.map(c => {
                                        const name = c.contactName || c.phoneNumber || 'Unknown';
                                        const initial = name.charAt(0).toUpperCase();
                                        const colorClass = getAvatarColor(name);
                                        return (
                                            <div
                                                key={c.id}
                                                className={`wac-contact-item ${selectedContact?.id === c.id ? 'active' : ''}`}
                                                onClick={() => handleContactClick(c)}
                                            >
                                                <div className={`wac-avatar ${colorClass}`}>{initial}</div>
                                                <div className="wac-contact-info">
                                                    <div className="wac-contact-name">{name}</div>
                                                    <div className="wac-contact-preview">
                                                        {c.lastMessageText || c.phoneNumber || 'Tap to view messages'}
                                                    </div>
                                                </div>
                                                <div className="wac-contact-meta">
                                                    {c.lastMessageTime && (
                                                        <span className="wac-contact-time">{formatTime(c.lastMessageTime)}</span>
                                                    )}
                                                    {c.unreadCount > 0 && (
                                                        <span className="wac-unread-badge">{c.unreadCount}</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* ── Main Chat Area ── */}
                        <div className="wac-main">
                            {selectedContact ? (
                                <>
                                    {/* Chat Header */}
                                    <div className="wac-chat-header">
                                        <div className={`wac-avatar size-lg ${getAvatarColor(selectedContact.contactName || selectedContact.phoneNumber || 'U')}`}>
                                            {(selectedContact.contactName || selectedContact.phoneNumber || 'U').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="wac-chat-header-info">
                                            <h4>{selectedContact.contactName || selectedContact.phoneNumber || 'Unknown'}</h4>
                                            <div className="wac-chat-header-sub">{selectedContact.phoneNumber}</div>
                                        </div>
                                        <div className="wac-header-actions">
                                            <button
                                                className="wac-header-action-btn"
                                                title="Refresh messages"
                                                onClick={() => { fetchContactsList(); fetchMessages(selectedContact.id, false); }}
                                            >
                                                <i className="bx bx-refresh"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Messages */}
                                    <div className="wac-messages">
                                        {messages.length === 0 && (
                                            <div className="wac-date-divider">
                                                <span>No messages yet. Say hello!</span>
                                            </div>
                                        )}
                                        {messages.map((m, index) => {
                                            const sent = isSentByMe(m.sender);
                                            
                                            let showDateDivider = false;
                                            let dateDividerText = "";
                                            
                                            if (index === 0) {
                                                showDateDivider = true;
                                                dateDividerText = formatDateDivider(m.creationTime);
                                            } else {
                                                const prevMessage = messages[index - 1];
                                                if (m.creationTime && prevMessage.creationTime) {
                                                    const prevDate = new Date(prevMessage.creationTime).toDateString();
                                                    const currDate = new Date(m.creationTime).toDateString();
                                                    if (prevDate !== currDate) {
                                                        showDateDivider = true;
                                                        dateDividerText = formatDateDivider(m.creationTime);
                                                    }
                                                } else if (m.creationTime && !prevMessage.creationTime) {
                                                    showDateDivider = true;
                                                    dateDividerText = formatDateDivider(m.creationTime);
                                                }
                                            }

                                            return (
                                                <React.Fragment key={m.id || index}>
                                                    {showDateDivider && dateDividerText && (
                                                        <div className="wac-date-divider">
                                                            <span>{dateDividerText}</span>
                                                        </div>
                                                    )}
                                                    <div className={`wac-msg-row ${sent ? 'sent' : 'received'}`}>
                                                        <div className="wac-msg-bubble">
                                                            <div className="wac-msg-text">{m.messageText}</div>
                                                            <div className="wac-msg-footer">
                                                                <span className="wac-msg-time">{formatMsgTime(m.creationTime)}</span>
                                                                {sent && getStatusIcon(m.status)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {/* Input */}
                                    <div className="wac-input-area">
                                        <div className="wac-input-wrap">
                                            <input
                                                className="wac-input"
                                                placeholder="Type a message..."
                                                value={replyText}
                                                onChange={e => setReplyText(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
                                            />
                                        </div>
                                        <button
                                            className="wac-send-btn"
                                            onClick={handleSendReply}
                                            disabled={!replyText.trim()}
                                            title="Send"
                                        >
                                            <i className="bx bx-send"></i>
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="wac-no-chat">
                                    <div className="wac-no-chat-icon">
                                        <i className="bx bxl-whatsapp" style={{ fontSize: 44, color: '#128c7e' }}></i>
                                    </div>
                                    <h3 className="wac-no-chat-title">Welcome to WhatsApp Chat</h3>
                                    <p className="wac-no-chat-sub">
                                        Select a conversation from the sidebar or start a new chat to begin messaging your contacts.
                                    </p>
                                    <button className="wac-no-chat-cta" onClick={() => setIsNewChatModalOpen(true)}>
                                        <i className="bx bx-message-add" style={{ marginRight: 6, verticalAlign: 'middle' }}></i>
                                        Start New Chat
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── New Chat Modal ── */}
            <Modal isOpen={isNewChatModalOpen} toggle={() => setIsNewChatModalOpen(false)} centered size="md">
                <div className="wac-modal-header-custom">
                    <div className="wac-modal-header-inner">
                        <div className="wac-modal-icon">
                            <i className="bx bx-message-add"></i>
                        </div>
                        <div>
                            <h5 className="wac-modal-title">Start New Chat</h5>
                            <p className="wac-modal-subtitle">Choose a contact to begin messaging</p>
                        </div>
                    </div>
                    <button className="wac-modal-close-btn" onClick={() => setIsNewChatModalOpen(false)}>
                        <i className="bx bx-x"></i>
                    </button>
                </div>

                <ModalBody className="wac-modal-body px-4 py-3">
                    {/* User Type */}
                    <div className="wac-form-field">
                        <label className="wac-form-label">
                            <i className="bx bx-group"></i>
                            User Type
                        </label>
                        <Dropdown
                            value={selectedUserType}
                            onChange={(e) => setSelectedUserType(e.value)}
                            options={[
                                { label: 'Employees', value: 'Employees' },
                                { label: 'Dealer', value: 'Dealer' },
                                { label: 'Customer', value: 'Customer' },
                                { label: 'Franchise', value: 'Franchise' },
                                { label: 'Supplier', value: 'Supplier' }
                            ]}
                            placeholder="Select User Type"
                            className="w-full md:w-20rem padding-none"
                            style={{ width: '100%', borderRadius: '8px', border: '1px solid #dcdfe4' }}
                            panelStyle={{ zIndex: 9999 }}
                            appendTo={document.body}
                            filter
                            resetFilterOnHide={true}
                        />
                    </div>

                    {/* Contact */}
                    {selectedUserType && (
                        <div className="wac-form-field">
                            <label className="wac-form-label">
                                <i className="bx bx-user"></i>
                                Select Contact
                            </label>
                            <Dropdown
                                value={selectedNewContactPhone}
                                onChange={(e) => setSelectedNewContactPhone(e.value)}
                                options={contactOptions.map((c: any) => ({
                                    label: c.name ? `${c.name} — ${c.phone}` : c.phone,
                                    value: c.phone
                                }))}
                                placeholder="— Choose a contact —"
                                className="w-full md:w-20rem padding-none"
                                style={{ width: '100%', borderRadius: '8px', border: '1px solid #dcdfe4' }}
                                panelStyle={{ zIndex: 9999 }}
                                appendTo={document.body}
                                filter
                                resetFilterOnHide={true}
                            />
                        </div>
                    )}

                    {/* Preview pill when contact chosen */}
                    {selectedNewContactPhone && (
                        <div className="wac-contact-preview-pill">
                            <div className={`wac-avatar color-2`} style={{ width: 32, height: 32, fontSize: 13, flexShrink: 0 }}>
                                {(contactOptions.find(c => c.phone === selectedNewContactPhone)?.name || selectedNewContactPhone).charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>
                                    {contactOptions.find(c => c.phone === selectedNewContactPhone)?.name || 'Contact'}
                                </div>
                                <div style={{ fontSize: 12, color: '#8a96a3' }}>{selectedNewContactPhone}</div>
                            </div>
                            <i className="bx bx-check-circle" style={{ marginLeft: 'auto', color: '#128c7e', fontSize: 20 }}></i>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter className="wac-modal-footer-custom">
                    <button className="wac-modal-cancel-btn" onClick={() => setIsNewChatModalOpen(false)}>
                        Cancel
                    </button>
                    <button
                        className="wac-modal-confirm-btn"
                        onClick={handleCreateNewChat}
                        disabled={!selectedNewContactPhone || isCreatingChat}
                    >
                        {isCreatingChat ? (
                            <><i className="bx bx-loader bx-spin" style={{ marginRight: 6 }}></i>Starting...</>
                        ) : (
                            <><i className="bx bx-send" style={{ marginRight: 6 }}></i>Start Chat</>
                        )}
                    </button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
}

export default WhatsappChat;